/**
 * VerdictProfile — expandable panel per verdict showing top 5 characteristics
 * with prevalence %, type badge, and top 3 creatives with reason text.
 */

import { useVerdictSummary } from '@/hooks/useDashboard';
import { fmtPct } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';
import type { VerdictCharacteristic, ProfileCreative } from '@/types/dashboard';

// ============================================================================
// CONSTANTS
// ============================================================================

const TYPE_BADGE_LABELS: Record<string, string> = {
  gate: 'Gate',
  dimension: 'Dim',
  taxonomy: 'Tax',
  kpi: 'KPI',
  placement: 'Plcmt',
  mediaType: 'Media',
};

const TYPE_BADGE_COLORS: Record<string, string> = {
  gate: 'bg-danger/10 text-danger',
  dimension: 'bg-brand/10 text-brand',
  taxonomy: 'bg-info/10 text-info',
  kpi: 'bg-success/10 text-success',
  placement: 'bg-warning/10 text-warning',
  mediaType: 'bg-muted text-secondary',
};

const VERDICT_COLORS: Record<string, string> = {
  Scale: '#22C55E',
  Fix: '#F59E0B',
  Kill: '#EF4444',
};

// ============================================================================
// COMPONENT
// ============================================================================

export interface VerdictProfileProps {
  verdict: string;
}

export function VerdictProfile({ verdict }: VerdictProfileProps) {
  const color = VERDICT_COLORS[verdict] ?? '#8B95A7';

  return (
    <div className="mt-3 border-t border-border-subtle pt-3">
      <div className="text-xs font-semibold text-secondary mb-2">
        Your {verdict} creatives tend to be:
      </div>

      {/* Characteristics placeholder — wired for real data */}
      <CharacteristicsList verdict={verdict} color={color} />

      {/* Top creatives placeholder */}
      <div className="text-xs font-semibold text-secondary mb-2 mt-4">
        Top {verdict} creatives:
      </div>
      <CreativesList verdict={verdict} color={color} />
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function CharacteristicsList({ verdict, color }: { verdict: string; color: string }) {
  const { filters } = useDashboardContext();
  const { data } = useVerdictSummary(filters);

  // Access characteristics from extended data if available
  const extendedData = data as Record<string, unknown> | undefined;
  const verdictProfiles = extendedData?.verdictProfiles as Array<{
    verdict: string;
    characteristics: VerdictCharacteristic[];
    topCreatives: ProfileCreative[];
  }> | undefined;

  const profile = verdictProfiles?.find((p) => p.verdict === verdict);
  const characteristics = profile?.characteristics?.slice(0, 5) ?? [];

  if (characteristics.length === 0) {
    return (
      <div className="text-xs text-secondary italic py-2">
        Analyze more creatives to reveal patterns.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {characteristics.map((char, i) => (
        <div key={i} className="flex items-center gap-2">
          {/* Type badge */}
          <span
            className={cn(
              'text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0',
              TYPE_BADGE_COLORS[char.type] ?? 'bg-muted text-secondary'
            )}
          >
            {TYPE_BADGE_LABELS[char.type] ?? char.type}
          </span>

          {/* Label */}
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-primary truncate">{char.label}</div>
          </div>

          {/* Prevalence bar + value */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <div className="w-16 h-1.5 bg-surface-subtle rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: `${char.prevalence}%`, backgroundColor: color }}
              />
            </div>
            <span className="text-xs text-secondary w-8 text-right">{fmtPct(char.prevalence)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function CreativesList({ verdict, color }: { verdict: string; color: string }) {
  const { filters } = useDashboardContext();
  const { data } = useVerdictSummary(filters);

  const extendedData = data as Record<string, unknown> | undefined;
  const verdictProfiles = extendedData?.verdictProfiles as Array<{
    verdict: string;
    characteristics: VerdictCharacteristic[];
    topCreatives: ProfileCreative[];
  }> | undefined;

  const profile = verdictProfiles?.find((p) => p.verdict === verdict);
  const creatives = profile?.topCreatives?.slice(0, 3) ?? [];

  if (creatives.length === 0) {
    return (
      <div className="text-xs text-secondary italic py-2">
        No creatives available yet.
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {creatives.map((creative) => (
        <div
          key={creative.creativeId}
          className="flex items-start gap-2 rounded-md hover:bg-surface-subtle px-1 -mx-1 transition-colors"
        >
          <span
            className="text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0"
            style={{ backgroundColor: `${color}20`, color }}
          >
            {creative.mediaType === 'video' ? 'VID' : 'IMG'}
          </span>
          <div className="min-w-0">
            <div className="text-xs font-medium text-primary truncate">{creative.name}</div>
            <div className="text-xs text-secondary truncate">{creative.reason}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
